import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Send, Loader2, X, Sparkles, Users, Target, Award, ArrowRight, CheckCircle2, Search, FileText, Clock, Paperclip, Pencil, Check, Plus, Trash2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { extractPdfTextInBrowser, isPdfFile } from "@/lib/extractPdfText";
import { useToast } from "@/hooks/use-toast";
import { AtMentionInput, extractMentionedLabels, type MentionableItem } from "@/components/AtMentionInput";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface CourseDesignData {
  title: string;
  description: string;
  courseDuration?: string;
  courseDepth?: string;
  learnerArchetype: string;
  successMeasures: string;
  certificationAlignment: string[];
  objectives: string[];
}

const PLACEHOLDER = "__TO_BE_SPECIFIED__";

/** Must match course-creation-chat edge function limit */
const MAX_MSG_CHARS = 20000;

interface CourseOutline {
  title?: string;
  description?: string;
  courseDuration?: string;
  courseDepth?: string;
  learnerArchetype?: string;
  successMeasures?: string;
  certificationAlignment?: string[];
  objectives?: string[];
  sections?: { title: string; description: string; topics: string[]; duration: string }[];
}

interface CourseCreationChatProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  workspaceId?: string;
  initialTopic?: string;
  initialFiles?: File[];
}

/** Map creation-dialogue outline to addie_outlines row so Course page can show it without "Generate outline". */
function mapOutlineToAddie(outline: {
  title?: string;
  description?: string;
  courseDuration?: string;
  courseDepth?: string;
  learnerArchetype?: string;
  successMeasures?: string;
  objectives?: string[];
  sections?: { title?: string; description?: string; topics?: string[] }[];
} | null) {
  if (!outline) return null;
  const analysis = outline.learnerArchetype || outline.description
    ? { learner_needs: outline.learnerArchetype ?? "", learning_context: outline.description ?? "", performance_gaps: "" }
    : null;
  const design = outline.title || (outline.objectives?.length ?? 0) > 0
    ? {
        learning_objectives: Array.isArray(outline.objectives) ? outline.objectives : [],
        assessment_strategy: "",
        instructional_approach: outline.courseDepth ?? outline.description ?? "",
      }
    : null;
  const sections = outline.sections ?? [];
  const development = sections.length > 0 || outline.description
    ? {
        content_structure: sections.length > 0
          ? sections.map((s) => `${s.title ?? "Section"}: ${(s.topics ?? []).join(", ")}`).join("\n\n")
          : outline.description ?? "",
        activities: sections.flatMap((s) => s.topics ?? []),
        materials_needed: [],
      }
    : null;
  const implementation = outline.courseDuration
    ? { delivery_method: "", timeline: outline.courseDuration, logistics: "" }
    : null;
  const evaluation = outline.successMeasures
    ? { formative_evaluation: "", summative_evaluation: "", success_metrics: [outline.successMeasures] }
    : null;
  return { analysis, design, development, implementation, evaluation };
}

const INITIAL_MESSAGE = `What do you want to learn about today?`;

const MAX_ADDITIONAL_QUESTIONS = 5;

/** Truncate messages to avoid 400 "Message content too long". Use 2950 to stay under both old (3000) and current (20000) edge function limits. */
const TRUNCATE_MSG_CHARS = 2950;

function truncateMessagesForApi(msgs: Message[]): Message[] {
  return msgs.map((m) => {
    if (typeof m.content === "string" && m.content.length > TRUNCATE_MSG_CHARS) {
      return { ...m, content: m.content.slice(0, TRUNCATE_MSG_CHARS) + "\n\n[... truncated for length ...]" };
    }
    return m;
  });
}

/** Max chars for user input validation (matches edge function when deployed). */
const MAX_MESSAGE_CHARS = 20000;

const QUESTION_BANK = [
  {
    key: "certificate_alignment",
    bubbleLabel: "Add certificate alignment",
    fullQuestion: "Does this course align with any certifications? (e.g., AWS, PMP, CompTIA, or industry-specific credentials)",
  },
  {
    key: "upload_materials",
    bubbleLabel: "Upload course materials",
    fullQuestion: "Would you like to upload any materials now? Use the paperclip to attach files, or skip and add them later.",
  },
  {
    key: "learner_archetype",
    bubbleLabel: "Define learner profile",
    fullQuestion: "Who is the target audience? (e.g., beginners, professionals, hobbyists, career changers)",
  },
  {
    key: "session_length",
    bubbleLabel: "Set course length",
    fullQuestion: "How long should the course be? (e.g., 2 hours, 4 weeks, 8 weeks)",
  },
  {
    key: "depth",
    bubbleLabel: "Set depth of materials",
    fullQuestion: "What level of depth? Quick overview, practical how-to, or comprehensive deep-dive?",
  },
  {
    key: "special_elements",
    bubbleLabel: "Add special topics",
    fullQuestion: "Are there any specific topics or elements you want to cover?",
  },
] as const;

const MAX_ATTACHMENTS_PER_MESSAGE = 5;
const MAX_FILE_SIZE_MB = 25;
const MAX_ATTACHMENT_SIZE_FOR_CHAT = 2 * 1024 * 1024; // 2MB for base64; PDFs use client extraction instead
const MAX_EXTRACTED_TEXT = 80000; // ~20k tokens when sending pre-extracted PDF text
const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx,.txt,.md,.ppt,.pptx,.png,.jpg,.jpeg,.csv";

async function filesToAttachments(files: File[]): Promise<{ name: string; base64?: string; mimeType: string; extractedText?: string }[]> {
  const result: { name: string; base64?: string; mimeType: string; extractedText?: string }[] = [];
  const supported = /\.(pdf|txt|md|csv|json|html|htm|xml)$/i;
  for (const f of files) {
    if (!supported.test(f.name)) continue;
    try {
      const isPdf = isPdfFile(f);
      // For PDFs: prefer client-side extraction (no size limit, more reliable than edge unpdf)
      if (isPdf) {
        const extractedText = await extractPdfTextInBrowser(f);
        if (extractedText) {
          result.push({
            name: f.name,
            mimeType: f.type || "application/pdf",
            extractedText: extractedText.substring(0, MAX_EXTRACTED_TEXT) + (extractedText.length > MAX_EXTRACTED_TEXT ? "\n\n[... truncated ...]" : ""),
          });
          continue;
        }
      }
      // For non-PDF or when PDF extraction failed: use base64 (2MB limit)
      if (f.size > MAX_ATTACHMENT_SIZE_FOR_CHAT) continue;
      const buffer = await f.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = "";
      const chunk = 8192;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
      }
      result.push({ name: f.name, base64: btoa(binary), mimeType: f.type || "" });
    } catch (e) {
      console.warn("Could not read file:", f.name, e);
    }
  }
  return result;
}

function nameMatchesMention(mentionLabel: string, fileName: string): boolean {
  const mentionNorm = mentionLabel.toLowerCase().trim();
  const nameNorm = (fileName || "").toLowerCase();
  const nameWithoutExt = nameNorm.replace(/\.[^.]+$/, "");
  return mentionNorm === nameWithoutExt || nameWithoutExt.includes(mentionNorm) || mentionNorm.includes(nameWithoutExt);
}

const CourseCreationChat = ({ open, onOpenChange, onSuccess, workspaceId, initialTopic, initialFiles }: CourseCreationChatProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: INITIAL_MESSAGE }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [courseDesignData, setCourseDesignData] = useState<CourseDesignData | null>(null);
  const [readyToCreate, setReadyToCreate] = useState(false);
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [createdCourseId, setCreatedCourseId] = useState<string | null>(null);
  const [gatheringMaterials, setGatheringMaterials] = useState(false);
  const [researchMaterials, setResearchMaterials] = useState<any[] | null>(null);
  const [generatingOutline, setGeneratingOutline] = useState(false);
  const [courseOutline, setCourseOutline] = useState<CourseOutline | null>(null);
  const [isGeneratingOutline, setIsGeneratingOutline] = useState(false);
  const [isEditingOutline, setIsEditingOutline] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [accumulatedFiles, setAccumulatedFiles] = useState<File[]>([]);
  const [askedQuestionKeys, setAskedQuestionKeys] = useState<Set<string>>(new Set());
  const [pendingQuestionKey, setPendingQuestionKey] = useState<string | null>(null);
  const [additionalQuestionsAnswered, setAdditionalQuestionsAnswered] = useState(0);
  const [showSuggestionBubbles, setShowSuggestionBubbles] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevOutlineRef = useRef<string>("");
  const initialTopicConsumedRef = useRef(false);
  const initialFilesConsumedRef = useRef(false);
  const [recentlyUpdatedFields, setRecentlyUpdatedFields] = useState<Set<string>>(new Set());
  const [autoResearching, setAutoResearching] = useState(false);
  const [autoResearchError, setAutoResearchError] = useState(false);
  const [autoResearchResults, setAutoResearchResults] = useState<any[] | null>(null);
  const [autoResearchImages, setAutoResearchImages] = useState<any[] | null>(null);
  const researchSavedRef = useRef(false);

  // Must be declared before useEffect that uses it (temporal dead zone fix)
  const displayOutline = courseOutline ?? (readyToCreate && courseDesignData
    ? {
        title: courseDesignData.title,
        description: courseDesignData.description,
        courseDuration: courseDesignData.courseDuration ?? undefined,
        courseDepth: courseDesignData.courseDepth,
        learnerArchetype: courseDesignData.learnerArchetype,
        successMeasures: courseDesignData.successMeasures,
        certificationAlignment: courseDesignData.certificationAlignment,
        objectives: courseDesignData.objectives,
        sections: courseDesignData.objectives?.map((obj, i) => ({
          title: `Module ${i + 1}`,
          description: obj,
          topics: [obj],
          duration: "TBD",
        })) ?? [],
      } as CourseOutline
    : null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      initialTopicConsumedRef.current = false;
      initialFilesConsumedRef.current = false;
      setMessages([{ role: "assistant", content: INITIAL_MESSAGE }]);
      setInput("");
      setCourseDesignData(null);
      setReadyToCreate(false);
      setCreatingCourse(false);
      setCreatedCourseId(null);
      setGatheringMaterials(false);
      setResearchMaterials(null);
      setGeneratingOutline(false);
      setCourseOutline(null);
      setIsEditingOutline(false);
      setAttachedFiles([]);
      setAccumulatedFiles([]);
      setAskedQuestionKeys(new Set());
      setPendingQuestionKey(null);
      setAdditionalQuestionsAnswered(0);
      setShowSuggestionBubbles(false);
      setRecentlyUpdatedFields(new Set());
      setAutoResearching(false);
      setAutoResearchError(false);
      setAutoResearchResults(null);
      setAutoResearchImages(null);
      researchSavedRef.current = false;
      prevOutlineRef.current = "";
    }
  }, [open]);

  // Allow a new topic from the assistant (e.g. on create page) to trigger the flow again
  useEffect(() => {
    if (initialTopic?.trim()) initialTopicConsumedRef.current = false;
  }, [initialTopic]);

  // Allow new initialFiles to re-trigger when prop changes
  useEffect(() => {
    if (initialFiles?.length) initialFilesConsumedRef.current = false;
  }, [initialFiles]);

  // When opened with initialTopic, run first-message flow.
  // If initialFiles are also provided (e.g. gap-analysis course), pre-load them and pass as attachments.
  useEffect(() => {
    if (!open || !initialTopic?.trim() || initialTopicConsumedRef.current) return;
    initialTopicConsumedRef.current = true;
    // Consume files here so the separate initialFiles effect doesn't also fire
    initialFilesConsumedRef.current = true;
    if (initialFiles?.length) setAccumulatedFiles(initialFiles);

    const topic = initialTopic.trim();
    const userMsg: Message = { role: "user", content: topic };
    const newMessages: Message[] = [{ role: "assistant", content: INITIAL_MESSAGE }, userMsg];
    setMessages(newMessages);
    setIsLoading(true);

    (async () => {
      try {
        const attachments = initialFiles?.length ? await filesToAttachments(initialFiles) : undefined;
        const { data, error } = await invokeFunction("course-creation-chat", {
          body: {
            messages: truncateMessagesForApi([userMsg]),
            suggestionMode: true,
            ...(attachments?.length && { attachments }),
          },
        });
        if (error) throw new Error(error.message || (error as { error?: string }).error || "Failed to send message");
        const backendSaysSequential = data.message && (
          /\d+\.\s+(Who|What|Does|How|Are|Let)/i.test(data.message) ||
          /Let's start by understanding/i.test(data.message)
        );
        const ack = !backendSaysSequential && data.message
          ? data.message
          : `Got it! I'll design a targeted course based on the competency gaps. Let me generate an outline now.`;
        setMessages((prev) => [...prev, { role: "assistant", content: ack }]);
        const updatedMessages: Message[] = [...newMessages, { role: "assistant" as const, content: ack }];
        if (data.courseDesignData) setCourseDesignData(data.courseDesignData);
        await generateInitialOutline(updatedMessages, attachments);
        setShowSuggestionBubbles(true);
      } catch (err) {
        toast({
          variant: "destructive",
          title: "Error",
          description: err instanceof Error ? err.message : "Failed to start creation",
        });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [open, initialTopic]); // eslint-disable-line react-hooks/exhaustive-deps

  // When opened with pre-loaded workspace materials, auto-send and generate outline
  useEffect(() => {
    if (!open || !initialFiles?.length || initialFilesConsumedRef.current) return;
    initialFilesConsumedRef.current = true;

    const fileNames = initialFiles.map((f) => f.name).join(", ");
    const topic =
      initialFiles.length === 1
        ? `Create a course from this uploaded material: ${fileNames}`
        : `Create a course from these ${initialFiles.length} uploaded materials: ${fileNames}`;

    const userMsg: Message = { role: "user", content: topic };
    const newMessages: Message[] = [{ role: "assistant", content: INITIAL_MESSAGE }, userMsg];
    setMessages(newMessages);
    setAccumulatedFiles(initialFiles);
    setIsLoading(true);

    (async () => {
      try {
        const attachments = await filesToAttachments(initialFiles);
        const { data, error } = await invokeFunction("course-creation-chat", {
          body: { messages: truncateMessagesForApi([userMsg]), suggestionMode: true, attachments },
        });
        if (error) throw new Error((error as { message?: string }).message || (error as { error?: string }).error || "Failed");
        const ack =
          data?.message ||
          `I'll build a course from ${initialFiles.length > 1 ? "these materials" : "this material"}. Analysing the content and generating an outline…`;
        setMessages((prev) => [...prev, { role: "assistant", content: ack }]);
        const updatedMessages: Message[] = [...newMessages, { role: "assistant" as const, content: ack }];
        if (data?.courseDesignData) setCourseDesignData(data.courseDesignData);
        await generateInitialOutline(updatedMessages, attachments);
        setShowSuggestionBubbles(true);
      } catch (err) {
        toast({
          variant: "destructive",
          title: "Error",
          description: err instanceof Error ? err.message : "Failed to start creation",
        });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [open, initialFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save research materials as soon as both a course ID and results exist.
  // Handles the case where research finishes after the user clicks "Create Course".
  useEffect(() => {
    if (!createdCourseId) return;
    if (!autoResearchResults?.length && !autoResearchImages?.length) return;
    if (researchSavedRef.current) return;
    researchSavedRef.current = true;

    if (autoResearchResults?.length) {
      for (const material of autoResearchResults) {
        supabase.from("materials").insert({
          project_id: createdCourseId,
          workspace_id: workspaceId || null,
          file_name: material.title || "Untitled Source",
          file_type: "url",
          storage_path: material.url || "",
          summary: [
            material.description || "",
            material.category ? `Category: ${material.category}` : "",
            material.relevance ? `Relevance: ${material.relevance}` : "",
            "Source: AI Research",
          ].filter(Boolean).join("\n\n"),
          text_content: null,
        }).then(({ error: e }) => { if (e) console.warn("Could not save source:", material.title, e); });
      }
    }
    if (autoResearchImages?.length) {
      for (const img of autoResearchImages) {
        supabase.from("materials").insert({
          project_id: createdCourseId,
          workspace_id: workspaceId || null,
          file_name: `Image from ${img.sourceTitle}`.slice(0, 255),
          file_type: "image",
          storage_path: img.url,
          summary: `Image sourced from: ${img.sourceUrl}\n\nCategory: ${img.category || ""}\nSource: AI Research`,
          text_content: null,
        }).then(({ error: e }) => { if (e) console.warn("Could not save image:", img.url, e); });
      }
    }
  }, [createdCourseId, autoResearchResults, autoResearchImages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect outline changes and animate updated fields
  useEffect(() => {
    if (!displayOutline) return;
    const curr = JSON.stringify({
      title: displayOutline.title,
      description: displayOutline.description,
      courseDuration: displayOutline.courseDuration,
      courseDepth: displayOutline.courseDepth,
      learnerArchetype: displayOutline.learnerArchetype,
      successMeasures: displayOutline.successMeasures,
      certificationAlignment: displayOutline.certificationAlignment ?? [],
      sections: displayOutline.sections ?? [],
    });
    const hadPrev = prevOutlineRef.current.length > 0;
    if (hadPrev && prevOutlineRef.current !== curr) {
      const prev = JSON.parse(prevOutlineRef.current) as Record<string, unknown>;
      const next = JSON.parse(curr) as Record<string, unknown>;
      const changed = new Set<string>();
      const metaKeys = ["title", "description", "courseDuration", "courseDepth", "learnerArchetype", "successMeasures", "certificationAlignment"];
      for (const k of metaKeys) {
        if (JSON.stringify(prev[k]) !== JSON.stringify(next[k])) changed.add(k);
      }
      const prevSections = (prev.sections ?? []) as unknown[];
      const nextSections = (next.sections ?? []) as unknown[];
      for (let i = 0; i < Math.max(prevSections.length, nextSections.length); i++) {
        if (JSON.stringify(prevSections[i]) !== JSON.stringify(nextSections[i])) {
          changed.add(`section-${i}`);
        }
      }
      setRecentlyUpdatedFields(changed);
      const t = setTimeout(() => setRecentlyUpdatedFields(new Set()), 600);
      prevOutlineRef.current = curr;
      return () => clearTimeout(t);
    }
    prevOutlineRef.current = curr;
  }, [displayOutline]);

  const handleAttachFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    const totalAfter = attachedFiles.length + files.length;
    if (totalAfter > MAX_ATTACHMENTS_PER_MESSAGE) {
      toast({
        variant: "destructive",
        title: "Too many files",
        description: `You can attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`,
      });
      return;
    }

    const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
    const validFiles: File[] = [];
    for (const file of files) {
      if (file.size > maxBytes) {
        toast({
          variant: "destructive",
          title: "File too large",
          description: `${file.name} exceeds ${MAX_FILE_SIZE_MB}MB. Skipped.`,
        });
        continue;
      }
      validFiles.push(file);
    }
    setAttachedFiles((prev) => [...prev, ...validFiles]);
  };

  const removeAttachedFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getAvailableBubbles = () => {
    const available = QUESTION_BANK.filter((q) => !askedQuestionKeys.has(q.key) && q.key !== pendingQuestionKey);
    return available.slice(0, 3);
  };

  const parseBubbleAnswer = (key: string, answer: string): Partial<CourseDesignData & CourseOutline> => {
    const normalized = answer.trim().toLowerCase();
    switch (key) {
      case "certificate_alignment":
        if (normalized === "no" || normalized === "none" || normalized === "n/a" || normalized === "") {
          return { certificationAlignment: [] };
        }
        const certs = answer.split(/[,;]| and | or /).map((s) => s.trim()).filter(Boolean);
        return { certificationAlignment: certs.length > 0 ? certs : [answer] };
      case "learner_archetype":
        return { learnerArchetype: answer };
      case "session_length":
        return { courseDuration: answer };
      case "depth":
        if (/overview|intro|quick/i.test(answer)) return { courseDepth: "overview" };
        if (/comprehensive|deep|thorough/i.test(answer)) return { courseDepth: "comprehensive" };
        return { courseDepth: "practical" };
      case "special_elements":
        const topics = answer.split(/[,;]| and | or /).map((s) => s.trim()).filter(Boolean);
        return { objectives: topics.length > 0 ? [...(courseDesignData?.objectives ?? []), ...topics] : undefined };
      case "upload_materials":
        return {}; // No structured field; user may attach files
      default:
        return {};
    }
  };

  const handleBubbleClick = (q: (typeof QUESTION_BANK)[number]) => {
    setPendingQuestionKey(q.key);
    setShowSuggestionBubbles(false);
    setMessages((prev) => [...prev, { role: "assistant", content: q.fullQuestion }]);
    setAskedQuestionKeys((prev) => new Set([...prev, q.key]));
  };

  const mapSyllabusToOutline = (s: {
    title?: string;
    learnerProfile?: string;
    timeline?: string;
    successCriteria?: string;
    learningObjectives?: string[];
    sections?: { title: string; description: string; topics: string[]; duration: string }[];
  }): CourseOutline => ({
    title: s.title,
    description: undefined,
    courseDuration: s.timeline,
    learnerArchetype: s.learnerProfile,
    successMeasures: s.successCriteria,
    certificationAlignment: undefined,
    objectives: s.learningObjectives,
    sections: s.sections,
  });

  const generateInitialOutline = async (msgs: Message[], attachmentsForOutline?: { name: string; base64?: string; mimeType: string; extractedText?: string }[]) => {
    setIsGeneratingOutline(true);
    try {
      // Try course-creation-chat first (has outline support)
      // Pass full messages so topic (in first user message) is included
      let outline: CourseOutline | null = null;
      const { data, error } = await invokeFunction("course-creation-chat", {
        body: {
          messages: truncateMessagesForApi(msgs),
          generateOutline: true,
          generateOutlinePhase: "initial",
          attachments: attachmentsForOutline,
        },
      });
      if (!error && data?.outline) {
        outline = data.outline as CourseOutline;
      }
      // Fallback: use course-design-chat (used on landing page, likely deployed)
      let fallbackError: string | null = null;
      if (!outline) {
        const fallback = await invokeFunction("course-design-chat", {
          body: {
            messages: truncateMessagesForApi(msgs),
            generateSyllabus: true,
            generateSyllabusPhase: "initial",
            attachments: attachmentsForOutline,
          } as { messages: Message[]; generateSyllabus: boolean; generateSyllabusPhase: string; attachments?: typeof attachmentsForOutline },
        });
        if (!fallback.error && fallback.data?.syllabus) {
          outline = mapSyllabusToOutline(fallback.data.syllabus);
        } else if (fallback.error) {
          fallbackError = (fallback.error as { message?: string })?.message || (fallback.error as { error?: string })?.error || "Fallback outline generation failed";
        }
      }
      if (outline) {
        setCourseOutline(outline);
        // Fire deep research in background — non-blocking
        const researchTopic =
          outline.title ||
          msgs.find((m) => m.role === "user")?.content?.replace(/\[Attached:.*?\]\s*/g, "").trim() ||
          "";
        if (researchTopic) {
          setAutoResearching(true);
          invokeFunction("deep-research", {
            body: { topic: researchTopic, courseTitle: researchTopic, workspaceId: workspaceId || null, projectId: null },
          })
            .then(({ data, error: resErr }) => {
              if (resErr) {
                console.error("Deep research error:", resErr);
                setAutoResearchError(true);
              } else {
                if (data?.materials?.length > 0) setAutoResearchResults(data.materials);
                if (data?.images?.length > 0) setAutoResearchImages(data.images);
                if (!data?.materials?.length && !data?.images?.length) {
                  console.warn("Deep research returned no results:", data);
                  setAutoResearchError(true);
                }
              }
            })
            .catch((err) => {
              console.error("Deep research invocation failed:", err);
              setAutoResearchError(true);
            })
            .finally(() => setAutoResearching(false));
        }
      } else {
        throw new Error(fallbackError || error?.message || (error as { error?: string })?.error || "Outline generation failed");
      }
    } catch (err) {
      console.error("Error generating outline:", err);
      toast({
        variant: "destructive",
        title: "Could not generate outline",
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setIsGeneratingOutline(false);
    }
  };

  const refineOutline = async (msgs: Message[], current: CourseOutline, attachmentsForRefine?: { name: string; base64?: string; mimeType: string; extractedText?: string }[]) => {
    if (!current) return;
    try {
      const currentSyllabus = {
        title: current.title,
        learnerProfile: current.learnerArchetype,
        timeline: current.courseDuration,
        successCriteria: current.successMeasures,
        learningObjectives: current.objectives,
        sections: current.sections,
      };
      let outline: CourseOutline | null = null;
      let lastError: string | null = null;
      const { data, error } = await invokeFunction("course-creation-chat", {
        body: {
          messages: truncateMessagesForApi(msgs.slice(1)),
          generateOutline: true,
          generateOutlinePhase: "refine",
          currentOutline: current,
          attachments: attachmentsForRefine,
        },
      });
      if (!error && data?.outline) outline = data.outline as CourseOutline;
      else if (error) lastError = (error as { message?: string }).message ?? (error as { error?: string }).error ?? "Outline refinement failed";
      if (!outline) {
        const fallback = await invokeFunction("course-design-chat", {
          body: {
            messages: truncateMessagesForApi(msgs.slice(1)),
            generateSyllabus: true,
            generateSyllabusPhase: "refine",
            currentSyllabus,
            attachments: attachmentsForRefine,
          },
        });
        if (!fallback.error && fallback.data?.syllabus) {
          outline = mapSyllabusToOutline(fallback.data.syllabus);
        } else if (fallback.error && !lastError) {
          lastError = (fallback.error as { message?: string }).message ?? (fallback.error as { error?: string }).error ?? "Fallback outline failed";
        }
      }
      if (outline) {
        setCourseOutline(outline);
        toast({ title: "Outline updated", description: "Your course outline has been updated with your inputs." });
      } else if (lastError) {
        toast({ variant: "destructive", title: "Could not update outline", description: lastError });
      }
    } catch (err) {
      console.error("Error refining outline:", err);
      toast({
        variant: "destructive",
        title: "Could not update outline",
        description: err instanceof Error ? err.message : "Something went wrong. Try again.",
      });
    }
  };

  const isOutlinePlaceholder = (v: string | string[] | null | undefined): boolean => {
    if (v == null || v === "") return true;
    if (Array.isArray(v)) return v.length === 0 || v.every((x) => String(x) === PLACEHOLDER || !x);
    return String(v) === PLACEHOLDER;
  };

  const updateOutline = (updates: Partial<CourseOutline>) => {
    const base = courseOutline ?? (courseDesignData ? {
      title: courseDesignData.title,
      description: courseDesignData.description,
      courseDuration: courseDesignData.courseDuration,
      courseDepth: courseDesignData.courseDepth,
      learnerArchetype: courseDesignData.learnerArchetype,
      successMeasures: courseDesignData.successMeasures,
      certificationAlignment: courseDesignData.certificationAlignment,
      objectives: courseDesignData.objectives,
      sections: courseDesignData.objectives?.map((obj, i) => ({
        title: `Module ${i + 1}`,
        description: obj,
        topics: [obj],
        duration: "TBD",
      })) ?? [],
    } : null);
    if (!base) return;
    const next = { ...base, ...updates };
    setCourseOutline(next);
    if (courseDesignData) {
      setCourseDesignData({
        ...courseDesignData,
        title: next.title ?? courseDesignData.title,
        description: next.description ?? courseDesignData.description,
        courseDuration: next.courseDuration ?? courseDesignData.courseDuration,
        courseDepth: next.courseDepth ?? courseDesignData.courseDepth,
        learnerArchetype: next.learnerArchetype ?? courseDesignData.learnerArchetype,
        successMeasures: next.successMeasures ?? courseDesignData.successMeasures,
        certificationAlignment: next.certificationAlignment ?? courseDesignData.certificationAlignment,
        objectives: next.objectives ?? courseDesignData.objectives,
      });
    }
  };

  const updateSection = (idx: number, section: { title: string; description: string; topics: string[]; duration: string }) => {
    const sections = [...(displayOutline?.sections ?? [])];
    sections[idx] = section;
    updateOutline({ sections });
  };

  const addSection = () => {
    const sections = [...(displayOutline?.sections ?? []), { title: "New Section", description: "", topics: [""], duration: "TBD" }];
    updateOutline({ sections });
  };

  const removeSection = (idx: number) => {
    const sections = (displayOutline?.sections ?? []).filter((_, i) => i !== idx);
    updateOutline({ sections });
  };

  const addTopic = (sectionIdx: number) => {
    const sections = [...(displayOutline?.sections ?? [])];
    const sec = sections[sectionIdx];
    if (sec) {
      sections[sectionIdx] = { ...sec, topics: [...sec.topics, ""] };
      updateOutline({ sections });
    }
  };

  const removeTopic = (sectionIdx: number, topicIdx: number) => {
    const sections = [...(displayOutline?.sections ?? [])];
    const sec = sections[sectionIdx];
    if (sec && sec.topics.length > 1) {
      sections[sectionIdx] = { ...sec, topics: sec.topics.filter((_, i) => i !== topicIdx) };
      updateOutline({ sections });
    }
  };

  const MAX_MESSAGE_CHARS = 20000; // Must match course-creation-chat edge function limit

  const sendMessage = async () => {
    if ((!input.trim() && attachedFiles.length === 0) || isLoading) return;

    const attachmentText =
      attachedFiles.length > 0
        ? `[Attached: ${attachedFiles.map((f) => f.name).join(", ")}]\n\n`
        : "";
    const messageContent = attachmentText + input.trim();

    if (messageContent.length > MAX_MESSAGE_CHARS) {
      toast({
        variant: "destructive",
        title: "Message too long",
        description: `Maximum ${MAX_MESSAGE_CHARS.toLocaleString()} characters. For long materials, attach files instead of pasting text.`,
      });
      return;
    }

    const userMessage: Message = { role: "user", content: messageContent };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setAccumulatedFiles((prev) => [...prev, ...attachedFiles]);
    setAttachedFiles([]);
    setIsLoading(true);

    const isFirstMessage = messages.length === 1;
    const isBubbleAnswer = !!pendingQuestionKey;

    // Include @-referenced accumulated files in attachments so the AI can read them
    const mentionedLabels = extractMentionedLabels(messageContent);
    const referencedAccumulated = accumulatedFiles.filter((f) =>
      mentionedLabels.some((l) => nameMatchesMention(l, f.name))
    );
    const allFilesForAttachments = [...new Set([...attachedFiles, ...referencedAccumulated])];

    try {
      const attachments = allFilesForAttachments.length > 0 ? await filesToAttachments(allFilesForAttachments) : undefined;

      if (isFirstMessage) {
        // First answer (topic): get ack and initial outline, then show bubbles
        const { data, error } = await invokeFunction("course-creation-chat", {
          body: {
            messages: truncateMessagesForApi(newMessages.slice(1)),
            suggestionMode: true,
            attachments,
          },
        });

        if (error) throw new Error(error.message || (error as { error?: string }).error || "Failed to send message");

        // Use backend response; fallback to topic-based suggestion if it looks like old sequential flow
        const topic = messageContent.replace(/\[Attached:.*?\]\s*/g, "").trim();
        const backendSaysSequential = data.message && (
          /\d+\.\s+(Who|What|Does|How|Are|Let)/i.test(data.message) ||
          /Let's start by understanding/i.test(data.message)
        );
        const ack = !backendSaysSequential && data.message
          ? data.message
          : `Great! A course on ${topic}. Does this align with any certifications, or who's the target audience?`;
        const updatedMessages: Message[] = [...newMessages, { role: "assistant" as const, content: ack }];
        setMessages((prev) => [...prev, { role: "assistant", content: ack }]);

        // Initial course design data and outline
        if (data.courseDesignData) {
          setCourseDesignData(data.courseDesignData);
        }
        await generateInitialOutline(updatedMessages, attachments);
        setShowSuggestionBubbles(true);
      } else if (isBubbleAnswer) {
        // Answer to a bubble question: parse, update outline, show next bubbles or finish
        const key = pendingQuestionKey;
        setPendingQuestionKey(null);

        const parsed = parseBubbleAnswer(key, messageContent);
        const nextAnswered = additionalQuestionsAnswered + 1;

        setAdditionalQuestionsAnswered(nextAnswered);

        // Update courseDesignData and outline
        if (Object.keys(parsed).length > 0) {
          const base = courseDesignData ?? {
            title: courseOutline?.title ?? "",
            description: courseOutline?.description ?? "",
            courseDuration: courseOutline?.courseDuration,
            courseDepth: courseOutline?.courseDepth,
            learnerArchetype: courseOutline?.learnerArchetype ?? "",
            successMeasures: courseOutline?.successMeasures ?? "To be refined in dashboard",
            certificationAlignment: courseOutline?.certificationAlignment ?? [],
            objectives: courseOutline?.objectives ?? [],
          };
          const merged = {
            ...base,
            ...parsed,
            certificationAlignment: parsed.certificationAlignment ?? base.certificationAlignment,
            objectives: parsed.objectives ?? base.objectives,
          };
          setCourseDesignData(merged);
          updateOutline(parsed);
        }

        // Refine outline with the new Q&A (async, outline will update when complete)
        // Include ALL accumulated files so materials from previous messages are considered
        const allFilesForRefine = [...new Set([...accumulatedFiles, ...attachedFiles])];
        const attachmentsForRefine = allFilesForRefine.length > 0 ? await filesToAttachments(allFilesForRefine) : undefined;
        const updatedMessages = [...newMessages];
        refineOutline(updatedMessages, courseOutline ?? ({} as CourseOutline), attachmentsForRefine).catch(() => {});

        if (nextAnswered >= MAX_ADDITIONAL_QUESTIONS) {
          const doneMsg: Message = {
            role: "assistant",
            content: "Your outline is ready! Review it on the right and click Create Course when you're happy with it.",
          };
          setMessages((prev) => [...prev, doneMsg]);
          setReadyToCreate(true);
          setCourseDesignData((prev) => prev ?? {
            title: courseOutline?.title ?? "",
            description: courseOutline?.description ?? "",
            courseDuration: courseOutline?.courseDuration,
            courseDepth: courseOutline?.courseDepth,
            learnerArchetype: courseOutline?.learnerArchetype ?? "",
            successMeasures: courseOutline?.successMeasures ?? "",
            certificationAlignment: courseOutline?.certificationAlignment ?? [],
            objectives: courseOutline?.objectives ?? [],
          });
        } else {
          const ackMsg: Message = {
            role: "assistant",
            content: "Got it, I've added that. Would you like to add anything else?",
          };
          setMessages((prev) => [...prev, ackMsg]);
          setShowSuggestionBubbles(true);
        }
      } else {
        // Legacy / fallback: sequential Q&A
        const { data, error } = await invokeFunction("course-creation-chat", {
          body: { messages: truncateMessagesForApi(newMessages.slice(1)), attachments },
        });

        if (error) throw new Error(error.message || (error as { error?: string }).error || "Failed to send message");

        const assistantMessage: Message = { role: "assistant", content: data.message };
        const updatedMessages = [...newMessages, assistantMessage];
        setMessages((prev) => [...prev, assistantMessage]);

        if (updatedMessages.length >= 3) {
          if (!courseOutline) {
            await generateInitialOutline(updatedMessages, attachments);
          } else {
            refineOutline(updatedMessages, courseOutline, attachments).catch(() => {});
          }
        }

        if (data.readyToCreate && data.courseDesignData) {
          setCourseDesignData(data.courseDesignData);
          setReadyToCreate(true);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send message",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCourse = async () => {
    const source = displayOutline ?? courseDesignData;
    if (!source) return;

    setCreatingCourse(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "You must be logged in to create a course",
        });
        return;
      }

      // Build outline to save once (used for Edge Function or client-side upsert)
      const outlineToSave = displayOutline ?? (courseDesignData ? {
        title: courseDesignData.title,
        description: courseDesignData.description,
        courseDuration: courseDesignData.courseDuration,
        courseDepth: courseDesignData.courseDepth,
        learnerArchetype: courseDesignData.learnerArchetype,
        successMeasures: courseDesignData.successMeasures,
        certificationAlignment: courseDesignData.certificationAlignment ?? [],
        objectives: courseDesignData.objectives ?? [],
        sections: [],
      } : null);
      const addieRow = mapOutlineToAddie(outlineToSave);

      const title = source.title ?? courseDesignData?.title ?? "Untitled Course";
      const description = source.description ?? courseDesignData?.description ?? "";
      const archetype = source.learnerArchetype ?? courseDesignData?.learnerArchetype ?? "";
      const successMeasures = source.successMeasures ?? courseDesignData?.successMeasures ?? "";
      const courseDepth = source.courseDepth ?? courseDesignData?.courseDepth;
      const certificationAlignment = source.certificationAlignment ?? courseDesignData?.certificationAlignment ?? [];
      const objectives = (source.objectives?.length ? source.objectives : courseDesignData?.objectives) ?? [];
      const objectivesJson = objectives.map((obj: string, i: number) => ({
        id: `obj-${i + 1}`,
        title: obj,
        description: obj,
      }));

      const learnerProfile = {
        archetype,
        successMeasures,
        courseDepth,
        certificationAlignment,
      };
      const insertPayload = {
        user_id: user.id,
        title,
        description,
        status: "draft",
        workspace_id: workspaceId || null,
        learner_profile: learnerProfile,
        objectives: objectivesJson,
      };

      let course: { id: string } | null = null;
      // Prefer Edge Function first to avoid 404 when DB has no create_learning_project RPC
      const { data: fnData, error: fnError } = await invokeFunction("create-learning-project", {
        body: {
          title,
          description,
          workspace_id: workspaceId || null,
          learner_profile: learnerProfile,
          objectives: objectivesJson,
          addie_outline: addieRow ?? undefined,
          creation_outline: outlineToSave ?? undefined,
        },
      });
      const fnNotFound =
        (fnError as { status?: number })?.status === 404 ||
        (typeof (fnError as { message?: string })?.message === "string" &&
          ((fnError as { message?: string }).message?.toLowerCase().includes("function") &&
            (fnError as { message?: string }).message?.toLowerCase().includes("not found")));
      if (!fnError && fnData?.course) {
        course = fnData.course as { id: string };
      }
      if (!course && (fnError && !fnNotFound)) {
        const msg =
          (fnError as { message?: string })?.message ||
          (fnData as { error?: string })?.error ||
          "Course creation failed. Deploy the Edge Function: npx supabase functions deploy create-learning-project";
        throw new Error(msg);
      }
      if (!course && fnNotFound) {
        // Fallback: try DB RPC if Edge Function is not deployed
        const rpcResult = await supabase.rpc("create_learning_project", {
          p_title: title,
          p_description: description,
          p_status: "draft",
          p_workspace_id: workspaceId || null,
          p_learner_profile: learnerProfile,
          p_objectives: objectivesJson,
        });
        const rpcError = rpcResult.error;
        const rpcData = rpcResult.data;
        if (!rpcError && rpcData) {
          course = Array.isArray(rpcData) ? rpcData[0] : rpcData;
        }
        if (!course && rpcError) {
          throw rpcError;
        }
      }
      if (!course) {
        // Last resort: direct insert (e.g. no RPC and Edge Function not deployed)
        const insertResult = await supabase
          .from("learning_projects")
          .insert(insertPayload)
          .select()
          .single();
        if (insertResult.error) throw insertResult.error;
        course = insertResult.data;
      }
      if (!course?.id) {
        throw new Error("No course returned from course creation");
      }

      setCreatedCourseId(course.id);

      // Research materials are saved by the useEffect that watches createdCourseId + autoResearchResults/Images.

      // Always persist the outline via Edge Function (service role) so it appears on the course page regardless of RPC/RLS
      const shouldSaveOutline = addieRow || outlineToSave;
      if (shouldSaveOutline) {
        const { error: saveOutlineError } = await invokeFunction("save-addie-outline", {
          body: {
            project_id: course.id,
            addie_outline: addieRow ?? {},
            creation_outline: outlineToSave ?? null,
          },
        });
        if (saveOutlineError) {
          // Fallback: try client-side insert or update (avoid upsert 400 in some setups)
          const payload = {
            project_id: course.id,
            analysis: addieRow?.analysis ?? null,
            design: addieRow?.design ?? null,
            development: addieRow?.development ?? null,
            implementation: addieRow?.implementation ?? null,
            evaluation: addieRow?.evaluation ?? null,
            creation_outline: outlineToSave ?? null,
          };
          const cleanPayload = JSON.parse(JSON.stringify(payload));
          const { data: existingOutline } = await supabase
            .from("addie_outlines")
            .select("id")
            .eq("project_id", course.id)
            .maybeSingle();
          const outlineError = existingOutline
            ? (await supabase.from("addie_outlines").update({
                analysis: cleanPayload.analysis,
                design: cleanPayload.design,
                development: cleanPayload.development,
                implementation: cleanPayload.implementation,
                evaluation: cleanPayload.evaluation,
                creation_outline: cleanPayload.creation_outline,
              }).eq("project_id", course.id)).error
            : (await supabase.from("addie_outlines").insert(cleanPayload)).error;
          if (outlineError) {
            console.error("Failed to save course outline:", saveOutlineError, outlineError);
            const msg = (saveOutlineError as { message?: string })?.message || (saveOutlineError as { error?: string })?.error || outlineError.message;
            toast({
              variant: "destructive",
              title: "Outline not saved",
              description: msg || "Course was created but the outline could not be saved. You can generate it from the course page.",
            });
          }
        }
      }

      // Upload any attached files from the chat as course materials
      if (accumulatedFiles.length > 0) {
        for (const file of accumulatedFiles) {
          const fileExt = file.name.split(".").pop() || "bin";
          const storagePath = `${user.id}/${course.id}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`;

          const { error: uploadError } = await supabase.storage
            .from("materials")
            .upload(storagePath, file);

          if (uploadError) {
            console.error("Error uploading attachment:", uploadError);
            toast({
              variant: "destructive",
              title: "Upload failed",
              description: `Could not upload ${file.name}. You can add it manually.`,
            });
            continue;
          }

          const { data: material, error: insertError } = await supabase
            .from("materials")
            .insert({
              project_id: course.id,
              workspace_id: workspaceId || null,
              file_name: file.name,
              file_type: file.type,
              storage_path: storagePath,
            })
            .select()
            .single();

          if (!insertError && material) {
            let clientExtractedText: string | null = null;
            if (isPdfFile(file)) {
              clientExtractedText = await extractPdfTextInBrowser(file);
            } else {
              // For txt, md, csv, etc. — extract text client-side so process-material skips server extraction
              const textExts = /\.(txt|md|csv|json|html|htm|xml)$/i;
              if (textExts.test(file.name)) {
                try {
                  const raw = await file.text();
                  if (raw && raw.length > 0) {
                    clientExtractedText = raw.length > 80000
                      ? raw.substring(0, 80000) + "\n\n[... truncated ...]"
                      : raw;
                  }
                } catch {
                  /* ignore */
                }
              }
            }
            const body: { materialId: string; storagePath: string; clientExtractedText?: string } = {
              materialId: material.id,
              storagePath,
            };
            if (clientExtractedText) body.clientExtractedText = clientExtractedText;
            supabase.functions
              .invoke("process-material", { body })
              .catch((err) => console.error("Background processing error:", err));
          }
        }
        setAccumulatedFiles([]);
      }

      toast({
        title: "Course created!",
        description:
          accumulatedFiles.length > 0
            ? "Your course and attached materials are ready."
            : "Now let's add your course materials.",
      });
    } catch (error: unknown) {
      console.error("Error creating course:", error);
      const err = error as { message?: string; status?: number; code?: string };
      const msg =
        (error instanceof Error && error.message) ||
        (typeof err?.message === "string" && err.message) ||
        "An error occurred";
      const is404 =
        err?.status === 404 ||
        err?.code === "PGRST204" ||
        (typeof msg === "string" && (msg.includes("404") || (msg.toLowerCase().includes("function") && msg.toLowerCase().includes("not found"))));
      const isPermissionError =
        typeof msg === "string" &&
        (msg.includes("403") || msg.includes("permission") || msg.includes("row-level") || msg.includes("policy") || msg.includes("JWT"));
      toast({
        variant: "destructive",
        title: "Error creating course",
        description: is404
          ? "Course creation is not set up yet. In your Supabase project, run the migration that adds create_learning_project (see supabase/migrations/20260304000000_ensure_learning_projects_insert_policy.sql), or run: npx supabase db push"
          : isPermissionError
            ? "Permission denied. Make sure you're signed in and that your account has access. If this continues, try signing out and back in."
            : msg,
      });
    } finally {
      setCreatingCourse(false);
    }
  };

  const handleGoToMaterials = () => {
    if (createdCourseId) {
      onOpenChange(false);
      onSuccess();
      navigate(`/project/${createdCourseId}`, { state: { fromCreation: true } });
    }
  };

  const handleGatherSourceMaterial = async () => {
    const topic = courseDesignData?.title || courseOutline?.title || messages.find((m) => m.role === "user")?.content?.replace(/\[Attached:.*?\]\s*/g, "").trim();
    if (!topic) {
      toast({
        variant: "destructive",
        title: "Topic required",
        description: "Please enter a course topic first.",
      });
      return;
    }

    setGatheringMaterials(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "You must be logged in",
        });
        return;
      }

      const { data, error } = await invokeFunction("deep-research", {
        body: {
          topic,
          courseTitle: topic,
          workspaceId: workspaceId || null,
          projectId: createdCourseId || null, // Link materials to course if it exists
        },
      });

      if (error) {
        console.error("Deep research error:", error);
        throw new Error(error.message || error.error || "Failed to gather materials. The function may not be deployed yet.");
      }

      if (!data || !data.materials) {
        throw new Error("No materials returned from research");
      }

      setResearchMaterials(data.materials || []);
      toast({
        title: "Materials gathered!",
        description: `Found ${data.count || data.materials?.length || 0} relevant sources`,
      });
    } catch (error) {
      console.error("Error gathering materials:", error);
      const errorMessage = error instanceof Error ? error.message : "An error occurred";
      
      // Check if it's a network/fetch error
      if (errorMessage.includes("fetch") || errorMessage.includes("Failed to fetch")) {
        toast({
          variant: "destructive",
          title: "Connection Error",
          description: "Unable to reach the server. Please check if the edge function is deployed and try again.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error gathering materials",
          description: errorMessage,
        });
      }
    } finally {
      setGatheringMaterials(false);
    }
  };

  const handleCreateCourseFromResearch = async () => {
    if (!researchMaterials || researchMaterials.length === 0 || !courseDesignData) return;

    setGeneratingOutline(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "You must be logged in to create a course",
        });
        return;
      }

      // Generate course outline from research
      const { data: outlineData, error: outlineError } = await invokeFunction("generate-course-outline", {
        body: {
          materials: researchMaterials,
          courseTitle: courseDesignData.title,
          topic: courseDesignData.title,
          courseDuration: courseDesignData.courseDuration,
          courseDepth: courseDesignData.courseDepth,
          workspaceId: workspaceId || null,
          _authToken: (await supabase.auth.getSession()).data.session?.access_token,
        },
      });

      if (outlineError) {
        console.error("Generate course outline error:", outlineError);
        throw new Error(outlineError.message || outlineError.error || "Failed to generate course outline. The function may not be deployed yet.");
      }

      if (!outlineData || !outlineData.courseOutline) {
        throw new Error("No course outline returned from generation");
      }

      const outline = outlineData.courseOutline;

      // Update existing course or create new one
      let course;
      if (createdCourseId) {
        // Update existing course
        const { data: updatedCourse, error: updateError } = await supabase
          .from("learning_projects")
          .update({
            title: outline.title || courseDesignData.title,
            description: outline.description || courseDesignData.description,
            learner_profile: {
              archetype: outline.learnerArchetype || courseDesignData.learnerArchetype,
              successMeasures: outline.successMeasures || courseDesignData.successMeasures,
            },
            objectives: outline.objectives?.map((obj: string, i: number) => ({
              id: `obj-${i + 1}`,
              title: obj,
              description: obj,
            })) || courseDesignData.objectives.map((obj, i) => ({
              id: `obj-${i + 1}`,
              title: obj,
              description: obj,
            })),
          })
          .eq("id", createdCourseId)
          .select()
          .single();

        if (updateError) throw updateError;
        course = updatedCourse;
        // Ensure createdCourseId is set (should already be set, but just in case)
        if (!createdCourseId) {
          setCreatedCourseId(course.id);
        }
      } else {
        // Create new course
        const { data: newCourse, error: courseError } = await supabase
          .from("learning_projects")
          .insert({
            user_id: user.id,
            title: outline.title || courseDesignData.title,
            description: outline.description || courseDesignData.description,
            status: "draft",
            workspace_id: workspaceId || null,
            learner_profile: {
              archetype: outline.learnerArchetype || courseDesignData.learnerArchetype,
              successMeasures: outline.successMeasures || courseDesignData.successMeasures,
            },
            objectives: outline.objectives?.map((obj: string, i: number) => ({
              id: `obj-${i + 1}`,
              title: obj,
              description: obj,
            })) || courseDesignData.objectives.map((obj, i) => ({
              id: `obj-${i + 1}`,
              title: obj,
              description: obj,
            })),
          })
          .select()
          .single();

        if (courseError) throw courseError;
        course = newCourse;
        setCreatedCourseId(course.id);
      }

      // Ensure materials are linked to the course
      // Materials gathered before course creation would have workspace_id but no project_id
      if (researchMaterials && researchMaterials.length > 0 && course.id) {
        const materialUrls = researchMaterials.map(m => m.url);
        
        // Find and link materials that match the research URLs
        const { data: existingMaterials, error: findError } = await supabase
          .from("materials")
          .select("id, storage_path, project_id")
          .in("storage_path", materialUrls);

        if (!findError && existingMaterials) {
          // Update materials that don't have a project_id yet (they were stored with workspace_id)
          const materialsToLink = existingMaterials
            .filter(m => !m.project_id)
            .map(m => m.id);

          if (materialsToLink.length > 0) {
            const { error: linkError, count } = await supabase
              .from("materials")
              .update({ project_id: course.id })
              .in("id", materialsToLink)
              .select();

            if (linkError) {
              console.error("Error linking materials to course:", linkError);
            } else {
              console.log(`Linked ${materialsToLink.length} research materials to course ${course.id}`);
            }
          }
        }

        // Also verify materials exist - if not, store them now with the course ID
        const { count: materialCount } = await supabase
          .from("materials")
          .select("id", { count: "exact", head: true })
          .eq("project_id", course.id)
          .in("storage_path", materialUrls);

        if (materialCount === 0) {
          console.log("No materials found for course, storing them now...");
          // Store materials with the course ID - only include valid schema fields
          const materialsToInsert = researchMaterials.map((material) => {
            const summary = `${material.description || 'No description'}\n\nCategory: ${material.category || 'General'}\nRelevance: ${material.relevance || 'Relevant to course topic'}\nSource: AI Research`;
            return {
              project_id: course.id,
              file_name: material.title || 'Untitled Material',
              file_type: "url",
              storage_path: material.url || '',
              summary: summary,
              text_content: null,
            };
          });

          // Insert materials one at a time to better handle errors
          let successCount = 0;
          let errorCount = 0;
          
          for (const material of materialsToInsert) {
            const { error: insertError } = await supabase
              .from("materials")
              .insert(material);
            
            if (insertError) {
              console.error("Error storing material:", insertError, material);
              errorCount++;
            } else {
              successCount++;
            }
          }
          
          if (errorCount > 0) {
            console.error(`Failed to store ${errorCount} materials, ${successCount} succeeded`);
          } else {
            console.log(`Stored ${successCount} materials for course ${course.id}`);
          }
        }
      }

      // Save the generated outline via Edge Function so the course manager shows it (no "Generate outline" needed)
      const addieRow = mapOutlineToAddie(outline);
      if (addieRow) {
        const { error: saveOutlineError } = await invokeFunction("save-addie-outline", {
          body: { project_id: course.id, addie_outline: addieRow, creation_outline: outline },
        });
        if (saveOutlineError) {
          const payload = {
            project_id: course.id,
            analysis: addieRow.analysis ?? null,
            design: addieRow.design ?? null,
            development: addieRow.development ?? null,
            implementation: addieRow.implementation ?? null,
            evaluation: addieRow.evaluation ?? null,
            creation_outline: outline ?? null,
          };
          const cleanPayload = JSON.parse(JSON.stringify(payload));
          const { data: existingOutline } = await supabase
            .from("addie_outlines")
            .select("id")
            .eq("project_id", course.id)
            .maybeSingle();
          const outlineError = existingOutline
            ? (await supabase.from("addie_outlines").update({
                analysis: cleanPayload.analysis,
                design: cleanPayload.design,
                development: cleanPayload.development,
                implementation: cleanPayload.implementation,
                evaluation: cleanPayload.evaluation,
                creation_outline: cleanPayload.creation_outline,
              }).eq("project_id", course.id)).error
            : (await supabase.from("addie_outlines").insert(cleanPayload)).error;
          if (outlineError) {
            console.error("Failed to save course outline:", saveOutlineError, outlineError);
            const msg = (saveOutlineError as { message?: string })?.message || (saveOutlineError as { error?: string })?.error || outlineError.message;
            toast({
              variant: "destructive",
              title: "Outline not saved",
              description: msg || "Course was created but the outline could not be saved. You can generate it from the course page.",
            });
          }
        }
      }

      // Small delay to ensure materials are stored before showing success
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast({
        title: "Course created!",
        description: `Your course has been created with ${researchMaterials.length} research materials. They're now available in your course materials section.`,
      });
    } catch (error) {
      console.error("Error creating course from research:", error);
      const errorMessage = error instanceof Error ? error.message : "An error occurred";
      
      // Check if it's a network/fetch error
      if (errorMessage.includes("fetch") || errorMessage.includes("Failed to fetch")) {
        toast({
          variant: "destructive",
          title: "Connection Error",
          description: "Unable to reach the server. Please check if the 'generate-course-outline' edge function is deployed and try again.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error creating course",
          description: errorMessage,
        });
      }
    } finally {
      setGeneratingOutline(false);
    }
  };

  if (!open) return null;

  const isWelcomePhase = messages.length === 1;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* Sticky header */}
      <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <span className="font-semibold text-sm">New Course</span>
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenChange(false)}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {isWelcomePhase ? (
        /* Welcome / centered input phase */
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-5">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-3 text-center">Design a course</h1>
          <p className="text-muted-foreground text-center max-w-lg mb-10 text-base">
            Describe the course you want to build, upload a syllabus or existing materials, and I&apos;ll help you structure it.
          </p>
          <div className="w-full max-w-2xl space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleAttachFiles}
              className="hidden"
            />
            {attachedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 px-1">
                {attachedFiles.map((file, index) => (
                  <Badge key={`${file.name}-${index}`} variant="secondary" className="gap-1 pr-1">
                    <span className="max-w-[120px] truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachedFile(index)}
                      className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex gap-2 items-center border rounded-xl bg-card px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-ring">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                title="Attach materials"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              {[...accumulatedFiles, ...attachedFiles].length > 0 ? (
                <AtMentionInput
                  value={input}
                  onChange={setInput}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                  placeholder="Describe your course or type @ to reference attached files…"
                  disabled={isLoading}
                  items={([...accumulatedFiles, ...attachedFiles] as File[]).map((f) => ({
                    id: f.name + f.size,
                    label: f.name,
                    searchLabel: f.name.replace(/\.[^.]+$/, ""),
                  })) as MentionableItem[]}
                  className="border-0 shadow-none focus-visible:ring-0 flex-1 bg-transparent"
                />
              ) : (
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Describe your course, paste a syllabus, or attach materials…"
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                  disabled={isLoading}
                  className="border-0 shadow-none focus-visible:ring-0 flex-1 bg-transparent"
                />
              )}
              <Button
                onClick={sendMessage}
                disabled={isLoading || (!input.trim() && attachedFiles.length === 0)}
                size="icon"
                className="h-8 w-8 shrink-0"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Supports PDF, Word, PowerPoint, images and more
            </p>
          </div>
        </div>
      ) : (
        /* Active phase: split layout */
        <div className="flex-1 flex min-h-0">
          {/* Left panel: chat */}
          <div className="w-full md:w-[400px] flex-shrink-0 flex flex-col border-r">
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[90%] rounded-2xl px-4 py-3 ${
                        message.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-muted rounded-2xl px-4 py-3 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm text-muted-foreground">Thinking…</span>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
            <div className="p-3 border-t shrink-0 bg-muted/20">
              {showSuggestionBubbles && !pendingQuestionKey && additionalQuestionsAnswered < MAX_ADDITIONAL_QUESTIONS && !readyToCreate && (
                <div className="mb-3 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Would you like to add:</p>
                  <div className="flex flex-wrap gap-2">
                    {getAvailableBubbles().map((q) => (
                      <Button
                        key={q.key}
                        variant="outline"
                        size="sm"
                        className="rounded-full text-xs h-8 px-3"
                        onClick={() => handleBubbleClick(q)}
                      >
                        {q.bubbleLabel}
                      </Button>
                    ))}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full text-xs h-8 px-3 text-muted-foreground"
                      onClick={() => {
                        setShowSuggestionBubbles(false);
                        setMessages((prev) => [
                          ...prev,
                          { role: "assistant", content: "Your outline is ready! Review it on the right and click Create Course when you're happy with it." },
                        ]);
                        setReadyToCreate(true);
                        setCourseDesignData((prev) => prev ?? {
                          title: courseOutline?.title ?? "",
                          description: courseOutline?.description ?? "",
                          courseDuration: courseOutline?.courseDuration,
                          courseDepth: courseOutline?.courseDepth,
                          learnerArchetype: courseOutline?.learnerArchetype ?? "",
                          successMeasures: courseOutline?.successMeasures ?? "",
                          certificationAlignment: courseOutline?.certificationAlignment ?? [],
                          objectives: courseOutline?.objectives ?? [],
                        });
                      }}
                    >
                      I&apos;m done
                    </Button>
                  </div>
                </div>
              )}
              {attachedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {attachedFiles.map((file, index) => (
                    <Badge key={`${file.name}-${index}`} variant="secondary" className="gap-1 pr-1">
                      <span className="max-w-[120px] truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeAttachedFile(index)}
                        className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                        aria-label={`Remove ${file.name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_FILE_TYPES}
                onChange={handleAttachFiles}
                className="hidden"
              />
              <div className="flex gap-2 items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="flex-shrink-0 text-muted-foreground hover:text-foreground"
                  title="Attach materials"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading}
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
                {([...accumulatedFiles, ...attachedFiles].length > 0 ? (
                  <AtMentionInput
                    value={input}
                    onChange={setInput}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                    placeholder="Type your response... Type @ to reference attached documents"
                    disabled={isLoading}
                    items={([...accumulatedFiles, ...attachedFiles] as File[]).map((f) => ({
                      id: f.name + f.size,
                      label: f.name,
                      searchLabel: f.name.replace(/\.[^.]+$/, ""),
                    })) as MentionableItem[]}
                    className="bg-background"
                  />
                ) : (
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your response…"
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                    disabled={isLoading}
                    className="bg-background flex-1"
                  />
                ))}
                <Button
                  onClick={sendMessage}
                  disabled={isLoading || (!input.trim() && attachedFiles.length === 0)}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Right panel: outline / research / success + persistent sources panel */}
          <div className="hidden md:flex flex-1 flex-col overflow-hidden">
            {/* Primary content */}
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              {researchMaterials && researchMaterials.length > 0 ? (
                <ScrollArea className="flex-1 p-6 overflow-auto">
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                        <FileText className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold mb-2">Source Materials Gathered</h3>
                      <p className="text-muted-foreground">
                        We found {researchMaterials.length} relevant sources for your course. Review them below, then create your course.
                      </p>
                    </div>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {researchMaterials.map((material, index) => (
                        <div key={index} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="secondary" className="text-xs">{material.category}</Badge>
                              </div>
                              <h4 className="font-semibold text-sm mb-1">{material.title}</h4>
                              <p className="text-xs text-muted-foreground mb-2">{material.description}</p>
                              <a href={material.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">{material.url}</a>
                              <p className="text-xs text-muted-foreground mt-2 italic">{material.relevance}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button variant="outline" className="flex-1" onClick={() => setResearchMaterials(null)}>Back</Button>
                      <Button className="flex-1 gap-2" onClick={handleCreateCourseFromResearch} disabled={generatingOutline}>
                        {generatingOutline ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating Course…</> : <><Sparkles className="h-4 w-4" /> Create Course</>}
                      </Button>
                    </div>
                  </div>
                </ScrollArea>
              ) : createdCourseId ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                  <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                    <CheckCircle2 className="h-8 w-8 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Course created!</h3>
                  <p className="text-muted-foreground mb-6 max-w-md">
                    Go to the course manager to continue. The assistant in the corner will be there to help you build the skill tree, add materials, and more.
                  </p>
                  <div className="flex flex-col gap-3 w-full max-w-sm">
                    <Button size="lg" onClick={handleGoToMaterials} className="gap-2">
                      <ArrowRight className="h-4 w-4" />
                      Go to course management page
                    </Button>
                    {autoResearching ? (
                      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Still gathering sources — they&apos;ll be saved automatically
                      </div>
                    ) : (
                      <Button size="lg" variant="outline" onClick={handleGatherSourceMaterial} disabled={gatheringMaterials} className="gap-2">
                        {gatheringMaterials ? <><Loader2 className="h-4 w-4 animate-spin" /> Gathering Materials…</> : <><Search className="h-4 w-4" /> Add sources with AI</>}
                      </Button>
                    )}
                  </div>
                </div>
              ) : displayOutline ? (
                <ScrollArea className="flex-1 p-6 overflow-auto">
                  <div className="space-y-6 max-w-3xl">
                    <div className={`flex items-start justify-between gap-4 ${recentlyUpdatedFields.has("title") || recentlyUpdatedFields.has("description") ? "animate-outline-pop" : ""}`}>
                      <div className="flex-1">
                        {isEditingOutline ? (
                          <>
                            <Input value={displayOutline.title ?? ""} onChange={(e) => updateOutline({ title: e.target.value })} placeholder="Course title" className="text-2xl font-bold h-auto py-2" />
                            <Textarea value={displayOutline.description ?? ""} onChange={(e) => updateOutline({ description: e.target.value })} placeholder="Course description" className="mt-2" />
                          </>
                        ) : (
                          <>
                            <h3 className="text-2xl font-bold mb-2">{displayOutline.title || "Course Outline"}</h3>
                            {displayOutline.description && !isOutlinePlaceholder(displayOutline.description) && <p className="text-muted-foreground">{displayOutline.description}</p>}
                          </>
                        )}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setIsEditingOutline((b) => !b)}>{isEditingOutline ? "Done" : "Edit"}</Button>
                    </div>
                    {displayOutline.sections?.map((section, i) => (
                      <Card key={i}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">{section.title}</CardTitle>
                          {section.description && <CardDescription>{section.description}</CardDescription>}
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {section.topics?.map((topic, j) => (
                            <div key={j} className="flex items-center gap-2 text-sm">
                              <span className="text-muted-foreground">•</span>
                              <span>{topic}</span>
                            </div>
                          ))}
                          {section.duration && <p className="text-xs text-muted-foreground pt-1">Duration: {section.duration}</p>}
                        </CardContent>
                      </Card>
                    ))}
                    <div className="flex gap-2">
                      <Button onClick={handleCreateCourse} disabled={creatingCourse || !displayOutline?.title} className="gap-2">
                        {creatingCourse ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</> : <><Check className="h-4 w-4" /> Create Course</>}
                      </Button>
                      <Button variant="outline" onClick={() => { setReadyToCreate(false); setCourseDesignData(null); setCourseOutline(null); }}>Back</Button>
                    </div>
                  </div>
                </ScrollArea>
              ) : isGeneratingOutline ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 gap-4">
                  <Loader2 className="h-10 w-10 animate-spin text-primary/60" />
                  <p className="text-muted-foreground text-sm">Building your course outline…</p>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                    <FileText className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground text-sm max-w-xs">
                    Your course outline will appear here as we chat.
                  </p>
                </div>
              )}
            </div>

            {/* Sources panel — docked at bottom, visible while researching or once results are in */}
            {(autoResearching || autoResearchError || (autoResearchResults && autoResearchResults.length > 0)) && !(researchMaterials && researchMaterials.length > 0) && (
              <div className="border-t shrink-0 flex flex-col" style={{ maxHeight: "260px" }}>
                <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/30 shrink-0">
                  {autoResearching ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">Finding sources…</span>
                    </>
                  ) : autoResearchError ? (
                    <>
                      <span className="text-xs text-muted-foreground">Source research unavailable for this topic.</span>
                    </>
                  ) : (
                    <>
                      <Search className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-medium">
                        {autoResearchResults!.length} source{autoResearchResults!.length !== 1 ? "s" : ""} consulted
                      </span>
                      <span className="text-xs text-muted-foreground ml-1">
                        {createdCourseId ? "— saved to course" : "— will be added to course"}
                      </span>
                    </>
                  )}
                </div>
                {(autoResearchResults?.length || autoResearchImages?.length) && (
                  <ScrollArea className="flex-1">
                    <div className="p-3 space-y-3">
                      {autoResearchResults?.map((m, idx) => (
                        <div key={idx} className="space-y-0.5">
                          <a
                            href={m.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-medium text-primary hover:underline leading-snug block"
                          >
                            {m.title}
                          </a>
                          {m.description && (
                            <p className="text-xs text-muted-foreground leading-snug line-clamp-2">{m.description}</p>
                          )}
                          {m.category && (
                            <span className="inline-block text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded mt-0.5">
                              {m.category}
                            </span>
                          )}
                        </div>
                      ))}
                      {autoResearchImages && autoResearchImages.length > 0 && (
                        <div className="pt-1">
                          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
                            Images found
                          </p>
                          <div className="grid grid-cols-3 gap-1.5">
                            {autoResearchImages.map((img, idx) => (
                              <a
                                key={idx}
                                href={img.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={img.sourceTitle}
                                className="block rounded overflow-hidden border border-border hover:opacity-80 transition-opacity"
                              >
                                <img
                                  src={img.url}
                                  alt={img.sourceTitle}
                                  className="w-full h-16 object-cover"
                                  onError={(e) => {
                                    (e.currentTarget.parentElement as HTMLElement).style.display = "none";
                                  }}
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseCreationChat;

